import { yupResolver } from '@hookform/resolvers/yup';
import { Col, Form, message, Row, Spin } from 'antd';
import {
  DvmExamOutput,
  useCreateDvmExamMutation,
  useGetDonorQuery,
  useUpdateDvmExamMutation,
} from 'gql/types';
import { FormItem } from 'modules/common/components';
import { GET_DVM_EXAM_LIST_QUERY } from 'modules/donor/graphql/queries';
import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import { dvmExamFormFields } from './fields';
import { dvmExamFormSchemaType, DvmExamFormSchemaType } from './schema';

type Props = {
  donorId?: string;
  dvmExam?: DvmExamOutput;
  hide: () => void;
  visible?: boolean;
  setIsFormValid: (isValid: boolean) => void;
  setIsFormDirty: (isDirty: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
};

export const DvmExamForm = forwardRef(
  (
    {
      donorId: passedDonorId,
      dvmExam,
      hide,
      setIsFormValid,
      setIsFormDirty,
      setIsLoading,
    }: Props,
    ref
  ) => {
    const { t } = useTranslation('donor.DvmExamForm');
    const params = useParams();
    const incomingDonorId = passedDonorId ? passedDonorId : params.id || '';
    const isEditMode = Boolean(dvmExam);

    const { data, loading: getDonorLoading } = useGetDonorQuery({
      variables: {
        input: {
          id: dvmExam?.donor.id || incomingDonorId,
        },
      },
      skip: !incomingDonorId && !dvmExam,
    });

    const donorId = dvmExam?.donor.id || data?.getDonor.id;
    const formFields = useMemo(() => dvmExamFormFields, [donorId]);

    const createDvmExamForm = useForm<DvmExamFormSchemaType>({
      resolver: yupResolver(
        yup.object().shape({
          ...dvmExamFormSchemaType.fields,
        })
      ),
      mode: 'onChange',
    });

    const {
      formState: { isValid, isDirty },
      control,
      reset,
      handleSubmit,
    } = createDvmExamForm;

    useEffect(() => {
      setIsFormValid(isValid);
    }, [isValid, setIsFormValid]);

    useEffect(() => {
      setIsFormDirty(isDirty);
    }, [isDirty, setIsFormDirty]);

    const [createDvmExam, { loading: createDvmExamLoading }] =
      useCreateDvmExamMutation({
        refetchQueries: [
          {
            query: GET_DVM_EXAM_LIST_QUERY,
          },
          'getDvmExamList',
        ],
        onCompleted: () => {
          message.success(t('dvmExamHasBeenSuccessfullyCreated'));
          hide();
        },
        onError: (error) => {
          message.error(error.message);
        },
      });

    const [updateDvmExam, { loading: updateDvmExamLoading }] =
      useUpdateDvmExamMutation({
        refetchQueries: [
          {
            query: GET_DVM_EXAM_LIST_QUERY,
          },
          'getDvmExamList',
        ],
        onCompleted: () => {
          message.success(t('dvmExamHasBeenSuccessfullyUpdated'));
          hide();
        },
        onError: (error) => {
          message.error(error.message);
        },
      });

    useEffect(() => {
      if (dvmExam) {
        reset({
          animalName: dvmExam.donor.name,
          veterinarian: dvmExam.performedBy.id,
          donorId: dvmExam.donor.shortId.toString(),
          examinationDate: new Date(dvmExam.examinationDate),
          notes: dvmExam.notes || '',
        });
      } else if (data?.getDonor) {
        reset({
          animalName: data.getDonor.name,
          veterinarian: '',
          donorId: data.getDonor.shortId.toString(),
        });
      }
    }, [data, dvmExam, reset]);

    useImperativeHandle(ref, () => ({
      createHandler: handleSubmit((formData) => {
        if (isEditMode && dvmExam) {
          updateDvmExam({
            variables: {
              input: {
                id: dvmExam.id,
                examinationDate: new Date(
                  formData.examinationDate
                ).toISOString(),
                performedById: formData.veterinarian,
                notes: formData.notes || undefined,
              },
            },
          });
        } else {
          createDvmExam({
            variables: {
              input: {
                donorId: donorId!,
                examinationDate: new Date(
                  formData.examinationDate
                ).toISOString(),
                performedById: formData.veterinarian,
                notes: formData.notes || undefined,
              },
            },
          });
        }
      }),
    }));

    const isLoading =
      createDvmExamLoading || getDonorLoading || updateDvmExamLoading;

    useEffect(() => {
      setIsLoading(isLoading);
    }, [isLoading, setIsLoading]);

    return (
      <Spin spinning={getDonorLoading}>
        <FormProvider {...createDvmExamForm}>
          <Form labelCol={{ span: 12 }} layout="vertical">
            <Row>
              <Col span={24}>
                {formFields.map(({ Component, label, name }) => {
                  const errorMessage =
                    createDvmExamForm.formState.errors?.[name]?.message;
                  return (
                    <Col span={24} key={name}>
                      <FormItem
                        label={label}
                        extra={errorMessage}
                        validateStatus={errorMessage && 'error'}
                      >
                        <Component control={control} name={name} />
                      </FormItem>
                    </Col>
                  );
                })}
              </Col>
            </Row>
          </Form>
        </FormProvider>
      </Spin>
    );
  }
);
